import { useEffect, useState } from 'react';
import { subscribeSlotsForExperience } from '@/data/availabilitySlotsRepo';
import { isSlotBookable, type AvailabilitySlotType } from '@/types/types_availabilitySlot';

/**
 * One experience's availability calendar, live.
 *
 * `bookable` rides along for the same reason `pendingCount` does on
 * `useBookingRequests`: both callers want it, and deriving it from the same
 * snapshot is what stops the picker and the "no availability" message from
 * disagreeing. It is computed against a clock read once per snapshot, which is
 * accurate to the minute and does not need to be better — the booking
 * transaction re-checks the slot's start against the server's clock, and that is
 * the check that decides.
 *
 * Handed no experience id, it reports an empty calendar rather than staying in
 * the loading state: an experience that is not booked off a calendar genuinely
 * has none, which is a different thing from not knowing yet.
 */
export function useAvailabilitySlots(experienceId: string | undefined) {
  // Both pieces of state carry the experience they are about, and `loading` is
  // then derived rather than stored. That is what makes switching experience
  // honest: the previous one's calendar is not shown for the moment before the new
  // subscription's first snapshot arrives, and its error is not shown either.
  const [loaded, setLoaded] = useState<{ experienceId: string; slots: AvailabilitySlotType[] } | null>(null);
  const [failed, setFailed] = useState<{ experienceId: string; message: string } | null>(null);

  useEffect(() => {
    if (!experienceId) return;
    return subscribeSlotsForExperience(
      experienceId,
      (list) => {
        setLoaded({ experienceId, slots: list });
        setFailed(null);
      },
      (e) => {
        console.error('Availability subscription failed', e);
        setFailed({ experienceId, message: 'Could not load availability.' });
      },
    );
  }, [experienceId]);

  const current = loaded?.experienceId === experienceId ? loaded : null;
  const error = failed && failed.experienceId === experienceId ? failed.message : null;
  const loading = !!experienceId && !current && !error;
  const slots = current?.slots ?? [];

  const now = new Date().toISOString();
  const bookable = slots.filter((slot) => isSlotBookable(slot, now));

  return { slots, bookable, loading, error };
}
